import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "../shell/PublicLayout";
import ClassMedia from "../classpage/ClassMedia";
import Overline from "../components/Overline";
import Button from "../components/Button";
import { getDailyShiur } from "../api/redesignApi";
import { fmt } from "../player/MiniPlayer";
import { paths } from "../routing/paths";

/*
 * Daily Tanya / Hayom Yom — the daily shiur in the individual-class design:
 * breadcrumbs, date overline, title + meta above the fold, player card +
 * source-text panel side-by-side. The daily endpoint can return more than
 * one class (e.g. leap-year splits); each gets the full class treatment.
 */

const PAGES = {
  tanya: { label: "Daily Tanya", apiName: "Tanya", blurb: "Today's Tanya shiur, following the daily Chitas cycle." },
  "hayom-yom": { label: "Hayom Yom", apiName: "Hayom Yom", blurb: "Today's Hayom Yom entry — a teaching for each day of the year." },
};

function LoadingState() {
  return (
    <div className="max-w-[1000px] w-full mx-auto px-5 pt-8 flex flex-col gap-4" aria-busy="true" aria-label="Loading">
      <div className="h-4 w-48 rounded bg-ic-lightGray animate-pulse" />
      <div className="h-10 w-3/4 rounded bg-ic-lightGray animate-pulse" />
      <div className="grid gap-5 mt-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))" }}>
        <div className="h-72 rounded-xl bg-ic-navy/90 animate-pulse" />
        <div className="h-72 rounded-xl bg-ic-lightGray animate-pulse" />
      </div>
    </div>
  );
}

function ShiurSection({ classData, headingLevel }) {
  const Heading = headingLevel;

  const contextParagraph = useMemo(() => {
    const p = classData.paragraphs[0];
    if (!p) return null;
    const text = p.text || "";
    return text.length > 400 ? `${text.slice(0, 400).trim()}…` : text;
  }, [classData.paragraphs]);

  const metaLine = [
    classData.audio?.durationSeconds ? fmt(classData.audio.durationSeconds) : null,
    [classData.audio && "Audio", classData.video && "Video", classData.files.length > 0 && "source text"]
      .filter(Boolean)
      .join(" + "),
    "Rabbi Yossi Paltiel",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <section className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <Heading
          className="m-0 font-extrabold text-black tracking-[-0.5px] ic-wrap-anywhere"
          style={{ fontSize: "clamp(26px, 6vw, 40px)", lineHeight: 1.2 }}
        >
          {classData.title}
        </Heading>
        {contextParagraph && (
          <p className="m-0 text-[18px] font-normal leading-7 text-ic-body">{contextParagraph}</p>
        )}
        <span className="text-[14px] leading-5 text-ic-slate">{metaLine}</span>
      </div>

      <ClassMedia classData={classData} seriesInfo={null} />

      {classData.paragraphs.length > 1 && (
        <div className="flex flex-col gap-3">
          <Overline>About this shiur</Overline>
          {classData.paragraphs.slice(1).map((p, i) => (
            <p key={i} className="m-0 text-[18px] leading-7 text-ic-body ic-wrap-anywhere">
              {p.text}
            </p>
          ))}
        </div>
      )}

      {classData.tags.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {classData.tags.map((t) => (
            <Link
              key={t.id}
              to={paths.tag(t.slug)}
              className="text-[14px] font-semibold leading-5 text-ic-body bg-white border border-ic-borderGray px-4 rounded-3xl no-underline min-h-[44px] box-border inline-flex items-center transition-all duration-150 ease-out hover:border-ic-blue hover:text-ic-blue hover:no-underline focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft"
            >
              {t.name}
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

export default function DailyShiurPage({ kind }) {
  const page = PAGES[kind] || PAGES.tanya;
  const [state, setState] = useState({ status: "loading", result: null });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading", result: null });
    getDailyShiur(page.apiName)
      .then((result) => !cancelled && setState({ status: "done", result }))
      .catch(() => !cancelled && setState({ status: "done", result: null }));
    return () => {
      cancelled = true;
    };
  }, [page.apiName]);

  const result = state.result;
  const classes = result?.classes || [];
  const date = result?.date || null;

  return (
    <PublicLayout>
      <main className="max-w-[1000px] w-full mx-auto px-5 pt-5 pb-10 flex flex-col gap-5">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-[14px] leading-5 whitespace-nowrap">
          <Link to={paths.home()} className="text-ic-blue font-semibold py-3 px-1 -my-3 inline-flex items-center min-h-[44px] hover:underline">
            Home
          </Link>
          <span className="text-ic-slate">→</span>
          <span className="text-ic-body font-semibold px-1">{page.label}</span>
        </nav>

        <div className="flex flex-col gap-1">
          <Overline>
            {date ? `Today · ${date.hebrew} · ${date.civil}` : "Today"}
          </Overline>
          <p className="m-0 text-[16px] leading-6 text-ic-slate">{page.blurb}</p>
        </div>

        {state.status === "loading" ? (
          <LoadingState />
        ) : classes.length > 0 ? (
          <div className="flex flex-col gap-12">
            {classes.map((c, i) => (
              <ShiurSection key={c.id || i} classData={c} headingLevel={i === 0 ? "h1" : "h2"} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4 py-16 text-center">
            <h1 className="m-0 text-[28px] font-bold leading-9 tracking-[-0.3px] text-black">
              Today's {page.label} isn't up yet
            </h1>
            <p className="m-0 text-[18px] leading-7 text-ic-body max-w-[480px]">
              Check back shortly — in the meantime, the full archive is a search away.
            </p>
            <Link to={paths.search()}>
              <Button size="lg">Search the archive</Button>
            </Link>
          </div>
        )}
      </main>
    </PublicLayout>
  );
}
